<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1180px"
    content-class="blog-editor-dialog"
    persistent
  >
    <div class="blog-editor-modal">
      <div class="blog-editor-header">
        <div class="blog-editor-header-left">
          <div class="blog-editor-icon">
            <i :class="isEdit ? 'mdi mdi-pencil-outline' : 'mdi mdi-plus'"></i>
          </div>

          <div>
            <div class="blog-editor-eyebrow">
              BLOG EDITOR
            </div>

            <h2 class="blog-editor-title">
              {{ isEdit ? 'Edit Blog' : 'Create New Blog' }}
            </h2>

            <div class="blog-editor-description">
              {{ isEdit ? 'Update your existing blog post.' : 'Write a new blog post from scratch.' }}
            </div>
          </div>
        </div>

        <button type="button" class="blog-editor-close" @click="handleClose">
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <div class="blog-editor-body">
        <div class="blog-editor-information">
          <div class="blog-editor-field">
            <label>Blog Title</label>
            <input v-model="form.title" type="text" placeholder="Enter blog title" />
          </div>

          <div class="blog-editor-field">
            <label>Short Description</label>
            <input v-model="form.text" type="text" placeholder="Enter short description" />
          </div>

          <div class="blog-editor-field blog-editor-field-image">
            <label>Cover Image</label>
            <div v-if="imagePreview" class="blog-editor-image-preview">
              <img :src="imagePreview" alt="Blog cover preview" />
              <button type="button" class="blog-editor-image-remove" @click="removeImage">
                <i class="mdi mdi-close"></i>
              </button>
            </div>

            <div
              v-else
              :class="['blog-drop-zone-upload', imageDragOver ? 'drop-zone-upload-over' : '']"
              @dragenter.prevent="imageDragOver = true"
              @dragover.prevent
              @dragleave.prevent="imageDragOver = false"
              @drop.prevent="handleDropImage"
            >
              <div class="drop-zone-upload-info">
                <i class="mdi mdi-image-outline drop-zone-icon"></i>
                <div class="drop-zone-title">
                  Drop cover image or click to input
                </div>
              </div>
              <input type="file" accept="image/*" @change="onImageChange" @click.stop />
            </div>
          </div>
        </div>

        <div class="blog-editor-workspace">
          <aside class="blog-editor-sidebar">
            <div class="blog-editor-toc-label">
              Table of Contents
            </div>

            <ul class="blog-editor-toc-list">
              <li
                v-for="(section, index) in sections"
                :key="index"
                :class="{ active: sectionIndex === index }"
                class="blog-editor-toc-item"
              >
                <span
                  class="blog-editor-toc-item-label"
                  :title="section.label || `Section ${index + 1}`"
                  @click="sectionIndex = index"
                >
                  {{ section.label || `Section ${index + 1}` }}
                </span>
                <i
                  v-if="sections.length > 1"
                  class="mdi mdi-close blog-editor-toc-item-remove"
                  @click.stop="removeSection(index)"
                ></i>
              </li>
            </ul>

            <button type="button" class="blog-editor-add-section" @click="addSection">
              <i class="mdi mdi-plus"></i>
              Add Table of Contents
            </button>
          </aside>

          <div class="blog-editor-main">
            <div v-if="sections.length && sections[sectionIndex]" class="blog-editor-content">
              <div class="blog-editor-content-header">
                <div>
                  <div class="blog-editor-content-label">
                    EDITING TABLE OF CONTENTS
                  </div>

                  <h3>
                    <input
                      v-model="sections[sectionIndex].label"
                      type="text"
                      class="blog-editor-section-label-input"
                      placeholder="Section title"
                    />
                  </h3>
                </div>
              </div>

              <div class="blog-editor-quill">
                <vue-editor
                  v-model="sections[sectionIndex].content"
                  :editor-toolbar="toolbar"
                  useCustomImageHandler
                  @image-added="$emit('image-added', ...arguments)"
                  placeholder="Start writing your blog content..."
                ></vue-editor>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="blog-editor-footer">
        <div class="blog-editor-footer-info">
          <i class="mdi mdi-information-outline"></i>
          <span>{{ isEdit ? 'Changes will update this blog post.' : 'New blog post will be added to the Blog section.' }}</span>
        </div>

        <div class="blog-editor-actions">
          <button type="button" class="blog-editor-btn blog-editor-btn-cancel" @click="handleClose">
            Cancel
          </button>

          <button
            type="button"
            class="blog-editor-btn blog-editor-btn-save"
            :disabled="saving"
            @click="handleSave"
          >
            <i class="mdi mdi-content-save-outline"></i>
            {{ saving ? 'Saving...' : (isEdit ? 'Update Blog' : 'Create Blog') }}
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { VueEditor } from "vue2-editor";

export default {
  name: 'BlogEditorDialog',
  components: { VueEditor },
  props: {
    value: { type: Boolean, default: false },
    mode: { type: String, default: 'create' },
    saving: { type: Boolean, default: false },
    toolbar: { type: Array, required: true },
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: { title: "", text: "" },
      imageFile: null,
      imagePreview: null,
      imageDragOver: false,
      sections: [],
      sectionIndex: 0
    };
  },
  computed: {
    isEdit() {
      return this.mode === 'edit';
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.initForm();
    }
  },
  methods: {
    initForm() {
      const hasPrefill =
        this.initialData &&
        (this.initialData.title ||
        this.initialData.text ||
        this.initialData.imageUrl ||
        (this.initialData.sections && this.initialData.sections.length));

      if (this.isEdit || hasPrefill) {
        this.form = {
          title: this.initialData.title || "",
          text: this.initialData.text || ""
        };
        this.sections = this.initialData.sections?.length
          ? this.initialData.sections
          : [{ label: "Content", content: "" }];
        this.imagePreview = this.initialData.imageUrl || null;
      } else {
        this.form = { title: "", text: "" };
        this.sections = [{ label: "Content", content: "" }];
        this.imagePreview = null;
      }
      this.imageFile = null;
      this.imageDragOver = false;
      this.sectionIndex = 0;
    },
    setImage(file) {
      if (!file || !file.type?.startsWith('image/')) {
        this.$emit('toast', { message: 'File harus berupa gambar', type: 'error' });
        return;
      }
      this.imageFile = file;
      this.imagePreview = URL.createObjectURL(file);
    },
    onImageChange(e) {
      const file = e.target.files?.[0];
      if (file) this.setImage(file);
      e.target.value = '';
    },
    handleDropImage(e) {
      this.imageDragOver = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.setImage(file);
    },
    removeImage() {
      if (this.imagePreview && this.imageFile) {
        URL.revokeObjectURL(this.imagePreview);
      }
      this.imageFile = null;
      this.imagePreview = null;
    },
    addSection() {
      this.sections.push({
        label: `Table of Contents ${this.sections.length + 1}`,
        content: ""
      });
      this.sectionIndex = this.sections.length - 1;
    },
    removeSection(index) {
      if (this.sections.length <= 1) {
        this.$emit('toast', { message: 'Minimal harus ada 1 section', type: 'error' });
        return;
      }
      this.sections.splice(index, 1);
      if (this.sectionIndex >= this.sections.length) {
        this.sectionIndex = this.sections.length - 1;
      }
    },
    handleClose() {
      this.$emit('input', false);
      this.$emit('close');
    },
    handleSave() {
      if (!this.form.title?.trim()) {
        this.$emit('toast', { message: 'Blog title wajib diisi', type: 'error' });
        return;
      }

      this.$emit('save', {
        id: this.initialData.id ?? null,
        title: this.form.title,
        text: this.form.text,
        imageFile: this.imageFile,
        imageRemoved: !this.imagePreview && !this.imageFile,
        sections: this.sections
      });
    }
  }
};
</script>

<style scoped>
.blog-editor-field-image {
  grid-column: 1 / -1;
}

.blog-drop-zone-upload {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 20px;
  border: 2px dashed #dfe3e6;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.drop-zone-upload:hover,
.drop-zone-upload-over {
  border-color: #001d6c;
  background: #f2f4f8;
}

.blog-drop-zone-upload input[type="file"] {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.drop-zone-upload-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #697077;
  pointer-events: none;
}

.drop-zone-icon {
  font-size: 28px;
  color: #a2a9b0;
}

.drop-zone-title {
  font-size: 13px;
  font-weight: 600;
}

.blog-editor-image-preview {
  position: relative;
  width: 100%;
  max-height: 220px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #dfe3e6;
}

.blog-editor-image-preview img {
  width: 100%;
  height: 100%;
  max-height: 220px;
  object-fit: cover;
  display: block;
}

.blog-editor-image-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  cursor: pointer;
  transition: background 0.2s ease;
}

.blog-editor-image-remove:hover {
  background: rgba(0, 0, 0, 0.8);
}

.blog-editor-image-remove i {
  font-size: 16px;
}

.blog-editor-dialog {
  border-radius: 18px !important;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(0, 29, 108, 0.18),
    0 8px 30px rgba(33, 39, 42, 0.12);
}

.blog-editor-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
}

.blog-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 26px;
  background: #ffffff;
  border-bottom: 1px solid #e4e7eb;
  flex-shrink: 0;
}

.blog-editor-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.blog-editor-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: #f2f4f8;
  color: #001d6c;
}

.blog-editor-icon i {
  font-size: 21px;
}

.blog-editor-eyebrow {
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.2px;
  color: #21272A;
}

.blog-editor-title {
  margin: 0;
  font-size: 21px;
  line-height: 1.3;
  font-weight: 750;
  color: #21272A;
}

.blog-editor-description {
  margin-top: 3px;
  font-size: 13px;
  color: #697077;
}

.blog-editor-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #697077;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.blog-editor-close i {
  font-size: 21px;
}

.blog-editor-close:hover {
  background: #f2f4f8;
  color: #21272a;
}

.blog-editor-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.blog-editor-information {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 18px;
  padding: 20px 26px;
  background: #fafbfc;
  border-bottom: 1px solid #e4e7eb;
}

.blog-editor-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.blog-editor-field label {
  font-size: 13px;
  font-weight: 700;
  color: #4d5358;
}

.blog-editor-field input {
  width: 100%;
  height: 42px;
  padding: 0 13px;
  border: 1px solid #dfe3e6;
  border-radius: 9px;
  background: #ffffff;
  color: #21272a;
  font-family: inherit;
  font-size: 13px;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.blog-editor-field input::placeholder {
  color: #a2a9b0;
}

.blog-editor-field input:focus {
  border-color: #8fa9d6;
  box-shadow:
    0 0 0 3px rgba(0, 29, 108, 0.07);
}

.blog-editor-workspace {
  display: flex;
  min-height: 420px;
  padding: 0 20px 22px;
}

.blog-editor-sidebar {
  flex: 0 0 260px;
  width: 260px;
  max-width: 260px;
  align-self: flex-start;
  padding: 15px 0px 0 10px;
  background: transparent;
  border-right: 1px solid #E4E7EB;
  overflow: hidden;
}

.blog-editor-toc-label {
  font-size: 18px;
  font-weight: 700;
  color: #21272A;
  letter-spacing: 0.6px;
  margin-bottom: 12px;
}

.blog-editor-toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  border-left: 2px solid #E4E7EB;
}

.blog-editor-toc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  padding: 8px 8px 8px 16px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #4D5358;
  cursor: pointer;
  overflow: hidden;
  transition:
    color 0.2s ease,
    border-color 0.2s ease;
}

.blog-editor-toc-item:hover {
  color: #21272A;
}

.blog-editor-toc-item.active {
  color: #21272A;
  font-weight: 700;
  border-left: 2px solid #21272A;
  margin-left: -2px;
  padding-left: 16px;
}

.blog-editor-toc-item-label {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.blog-editor-toc-item-remove {
  font-size: 15px;
  color: #a2a9b0;
  cursor: pointer;
  flex: 0 0 auto;
  width: 15px;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    color 0.15s ease;
}

.blog-editor-toc-item:hover .blog-editor-toc-item-remove {
  opacity: 1;
}

.blog-editor-toc-item-remove:hover {
  color: #d92d20;
}

.blog-editor-add-section {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 8px 12px;
  background: transparent;
  border: 1px dashed #dfe3e6;
  border-radius: 8px;
  color: #4d5358;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.blog-editor-add-section:hover {
  background: #f2f4f8;
  border-color: #a2a9b0;
}

.blog-editor-add-section i {
  font-size: 15px;
}

.blog-editor-empty-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 8px 16px;
  border-left: 2px solid #E4E7EB;
  color: #697077;
  font-size: 14px;
}

.blog-editor-empty-section i {
  font-size: 17px;
}

.blog-editor-main {
  flex: 1;
  min-width: 0;
  background: #ffffff;
}

.blog-editor-content {
  display: flex;
  flex-direction: column;
}

.blog-editor-content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #e4e7eb;
}

.blog-editor-content-label {
  margin-bottom: 3px;
  font-size: 12px;
  color: #4d5358;
  font-weight: 700;
  letter-spacing: 1px;
}

.blog-editor-content-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #21272a;
}

.blog-editor-section-label-input {
  border: none;
  outline: none;
  font-size: 17px;
  font-weight: 700;
  color: #21272a;
  background: transparent;
  padding: 0;
  width: 100%;
  font-family: inherit;
}

.blog-editor-section-label-input::placeholder {
  color: #a2a9b0;
  font-weight: 400;
}

.blog-editor-quill {
  flex: 1;
  padding: 20px 22px 24px;
  background: #ffffff;
}

.blog-editor-quill .quillWrapper {
  display: flex;
  flex-direction: column;
  border: 1px solid #dfe3e6;
  border-radius: 11px;
  overflow: hidden;
  box-shadow:
    0 2px 8px rgba(33, 39, 42, 0.04);
}

.blog-editor-quill .ql-toolbar.ql-snow {
  border: 0;
  border-bottom: 1px solid #e4e7eb;
  background: #fafbfc;
  padding: 9px 11px;
}

.blog-editor-quill .ql-container.ql-snow {
  border: 0;
  font-family: inherit;
  font-size: 15px;
  color: #21272a;
}

.blog-editor-quill .ql-editor {
  min-height: 260px;
  padding: 18px 20px;
  line-height: 1.8;
}

.blog-editor-quill .ql-editor h1 {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
}

.blog-editor-quill .ql-editor h2 {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
}

.blog-editor-quill .ql-editor h3 {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 4px;
}

.blog-editor-quill .ql-editor.ql-blank::before {
  color: #a2a9b0;
  font-style: normal;
}

.blog-editor-quill .ql-snow .ql-stroke {
  stroke: #697077;
}

.blog-editor-quill .ql-snow .ql-fill {
  fill: #697077;
}

.blog-editor-quill .ql-snow .ql-picker {
  color: #697077;
}

.blog-editor-quill .ql-snow .ql-picker-label:hover,
.blog-editor-quill .ql-snow .ql-picker-item:hover,
.blog-editor-quill .ql-snow button:hover .ql-stroke,
.blog-editor-quill .ql-snow button.ql-active .ql-stroke {
  color: #001d6c;
  stroke: #001d6c;
}

.blog-editor-no-content {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.blog-editor-no-content-icon {
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  border-radius: 15px;
  background: #f2f4f8;
  color: #697077;
}

.blog-editor-no-content-icon i {
  font-size: 27px;
}

.blog-editor-no-content h3 {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #21272a;
}

.blog-editor-no-content p {
  margin: 0;
  font-size: 13px;
  color: #697077;
}

.blog-editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 22px;
  border-top: 1px solid #e4e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.blog-editor-footer-info {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #697077;
}

.blog-editor-footer-info i {
  font-size: 17px;
}

.blog-editor-actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

.blog-editor-btn {
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.blog-editor-btn-cancel {
  border: 1px solid #dfe3e6;
  background: #ffffff;
  color: #4d5358;
}

.blog-editor-btn-cancel:hover {
  background: #f7f8fa;
  color: #21272a;
}

.blog-editor-btn-save {
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
  box-shadow:
    0 3px 8px rgba(17, 24, 39, 0.14);
}

.blog-editor-btn-save:hover {
  background: #1f2937;
  transform: translateY(-1px);
  box-shadow:
    0 5px 12px rgba(17, 24, 39, 0.18);
}

.blog-editor-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.blog-editor-btn-save i {
  font-size: 15px;
}
</style>